import React, { useState, useEffect, useCallback } from 'react';
import { adminApi } from '../../services/api';
import AgGridTable from '../../components/AgGridTable';
import { useAuthContext } from '../../context/AuthContext';

const ScopeManagementPage = () => {
  const { user } = useAuthContext();
  const [resourcesLoading, setResourcesLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resources, setResources] = useState([]);
  const [currentUserScopes, setCurrentUserScopes] = useState([]);

  // Fetch available resources from backend
  const fetchResources = useCallback(async () => {
    try {
      setResourcesLoading(true);
      setError(null);

      const response = await adminApi.get('/admin/scopes/resources/');

      if (response.data) {
        // Transform categorized resources into flat array format
        const resourcesArray = [];

        Object.entries(response.data).forEach(([categoryName, resourcesList]) => {
          if (Array.isArray(resourcesList)) {
            resourcesList.forEach(resourceInfo => {
              resourcesArray.push({
                id: resourceInfo.resource,
                resource: resourceInfo.resource,
                description: resourceInfo.description || '',
                category: resourceInfo.category || categoryName,
                visibility: resourceInfo.visibility !== false,
                cruds: resourceInfo.cruds || '',
                views: resourceInfo.views || []
              });
            });
          }
        });

        setResources(resourcesArray);
      }
    } catch (err) {
      console.error('Failed to fetch resources:', err);
      setError(err.response?.data?.message || 'Failed to fetch resources. Using cached data.');
    } finally {
      setResourcesLoading(false);
    }
  }, []);

  // Parse current user's scopes from JWT
  useEffect(() => {
    if (user) {
      const token = localStorage.getItem('admin_api_token');
      if (token) {
        try {
          const base64Url = token.split('.')[1];
          const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
          const jsonPayload = decodeURIComponent(
            atob(base64)
              .split('')
              .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
              .join('')
          );
          const payload = JSON.parse(jsonPayload);

          const scopeString = payload.scope || '';
          const scopes = scopeString.split(' ').filter(s => s.length > 0);
          setCurrentUserScopes(scopes);
        } catch (err) {
          console.error('Failed to decode JWT:', err);
        }
      }
    }
  }, [user]);

  useEffect(() => {
    fetchResources();
  }, [fetchResources]);

  // Column definitions for resources table
  const resourceColumnDefs = [
    {
      headerName: 'Resource',
      field: 'resource',
      flex: 1,
      cellRenderer: (params) => (
        <div>
          <strong>{params.value}</strong>
        </div>
      )
    },
    {
      headerName: 'Description',
      field: 'description',
      flex: 2
    },
    {
      headerName: 'Category',
      field: 'category',
      width: 150,
      cellRenderer: (params) => (
        <span className="badge bg-info">{params.value}</span>
      )
    },
    {
      headerName: 'Available Operations',
      field: 'cruds',
      width: 180,
      cellRenderer: (params) => {
        const cruds = params.value || '';
        if (!cruds) {
          return <span className="text-muted">None defined</span>;
        }

        const operationLabels = {
          'c': 'Create',
          'r': 'Read',
          'u': 'Update',
          'd': 'Delete',
          's': 'Special'
        };

        const operations = cruds.split('').map(op => operationLabels[op] || op);

        return (
          <div className="d-flex flex-wrap gap-1">
            {operations.map((op, idx) => (
              <span key={idx} className="badge bg-light text-dark" style={{ fontSize: '0.75rem' }}>
                {op}
              </span>
            ))}
          </div>
        );
      }
    },
    {
      headerName: 'Your Access',
      field: 'your_access',
      width: 150,
      valueGetter: (params) => {
        const resourceName = params.data?.resource;
        if (!resourceName) return '';

        // Find matching scope
        const matchingScope = currentUserScopes.find(s => s.startsWith(resourceName + '.'));
        if (!matchingScope) return 'No Access';

        const cruds = matchingScope.split('.')[1] || '';
        return cruds;
      },
      cellRenderer: (params) => {
        const value = params.value;
        if (!value || value === 'No Access') {
          return <span className="badge bg-secondary">No Access</span>;
        }

        const labels = {
          'cruds': 'Full Access',
          'crud': 'Standard',
          'cru': 'Staff',
          'cr': 'Limited',
          'ru': 'Edit Only',
          'r': 'Read-Only'
        };

        return (
          <span className="badge bg-success">
            {labels[value] || value.toUpperCase()}
          </span>
        );
      }
    }
  ];

  return (
    <>
      {/* Page Title */}
      <div className="row">
        <div className="col-12">
          <div className="page-title-box d-sm-flex align-items-center justify-content-between">
            <h4 className="mb-sm-0 font-size-18">Scope Management (Read-Only)</h4>
            <div className="page-title-right">
              <ol className="breadcrumb m-0">
                <li className="breadcrumb-item"><span>Admin</span></li>
                <li className="breadcrumb-item active">Scopes</li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      {/* Info Alert */}
      <div className="alert alert-info mb-4" role="alert">
        <i className="fas fa-info-circle me-2"></i>
        <strong>Note:</strong> This page shows available resource scopes. To assign scopes to users, edit their role&apos;s permissions in the <a href="/admin/roles" className="alert-link">Roles</a> or <a href="/admin/sales/roles" className="alert-link">Sales Roles</a> pages.
      </div>

      {/* Current User Scopes */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <h5 className="card-title mb-0">Your Current Scopes</h5>
            </div>
            <div className="card-body">
              {currentUserScopes.length === 0 ? (
                <p className="text-muted mb-0">
                  {user?.isAnkrAdmin
                    ? 'You have unrestricted access (Admin/Ankr user)'
                    : 'No scopes assigned'}
                </p>
              ) : (
                <div className="d-flex flex-wrap gap-2">
                  {currentUserScopes.map((scope, idx) => (
                    <span key={idx} className="badge bg-primary">
                      {scope}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-warning alert-dismissible fade show" role="alert">
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError(null)}
          ></button>
        </div>
      )}

      {/* Available Resources */}
      <div className="card mb-4">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="card-title mb-0">Available Resources</h5>
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={fetchResources}
            disabled={resourcesLoading}
          >
            <i className="fas fa-sync me-1"></i>
            {resourcesLoading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
        <div className="card-body">
          {resourcesLoading && resources.length === 0 ? (
            <div className="text-center">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading resources...</span>
              </div>
            </div>
          ) : (
            <AgGridTable
              rowData={resources}
              columnDefs={resourceColumnDefs}
              domLayout="autoHeight"
              defaultColDef={{
                resizable: true,
                sortable: true,
                filter: true,
              }}
              autoSizeStrategy={{
                type: 'fitGridWidth'
              }}
              getRowId={(params) => params.data.id}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default ScopeManagementPage;
